import { createSelector } from 'reselect'
import { getLeAccommodationItems } from 'checkout/selectors/view/accommodation'
import getBedbankItems from 'checkout/selectors/view/items/getBedbankItems'
import { getFullOffers } from 'selectors/offerSelectors'
import { isBedbankOffer, isCruiseOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import { getFlightItems } from 'checkout/selectors/view/flights'
import { getJourneyV2IdKey } from 'lib/flights/flightUtils'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import { getCruiseCountriesByItinerary } from 'lib/cruises/cruiseUtils'
import { convertTwelveHourTimeToTimeStr } from 'lib/datetime/time'
import { findByTwoLetterCode } from 'lib/insurance/insuranceCountries'
import getTourV2CountryCodesVisited from 'lib/tours/getTourV2CountryCodesVisited'

function sanitizeCruiseTime(time: string | undefined): string {
  if (!time || time === '--') return ''
  const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return time
  return convertTwelveHourTimeToTimeStr(`${match[1]}:${match[2]}${match[3].toUpperCase()}`)
}

const getPassportCardQuoteItems = createSelector(
  (state: App.State) => getLeAccommodationItems(state),
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.cruiseDeparture,
  (
    leAccommodationItems,
    bedbankItems,
    flightItems,
    tourV2Items,
    offers,
    bedbankOffers,
    journeysById,
    cruiseItems,
    cruiseDepartureMap,
  ): Array<App.PassportCardQuoteItem> => {
    const leAccommodationQuoteItems = leAccommodationItems.flatMap((item) => {
      if (item.itemType !== 'hotel' || !('checkIn' in item) || !('checkOut' in item) || !item.checkIn || !item.checkOut) {
        return []
      }

      const offer = offers[item.offerId] as App.Offer | undefined
      const geoData = offer?.property?.geoData
      const street = [geoData?.streetNumber, geoData?.route]
        .filter(Boolean)
        .join(' ')
        .trim() || offer?.property?.address || ''

      return [{
        dp: 'accommodation',
        checkin: item.checkIn,
        checkout: item.checkOut,
        accommodationType: 'HOTEL',
        name: offer?.name ?? '',
        address: {
          street,
          country: geoData?.countryCode ?? '',
          zip: '',
          city: geoData?.locality ?? '',
        },
      }]
    })

    const bedbankQuoteItems = bedbankItems.flatMap((item) => {
      if (!item.checkIn || !item.checkOut) {
        return []
      }

      const checkoutOffer = offers[item.offerId]
      const offer = (isBedbankOffer(checkoutOffer) ? checkoutOffer : undefined)
        ?? bedbankOffers[item.offerId]
      const street = [offer?.property?.address.line1, offer?.property?.address.line2]
        .filter(Boolean)
        .join(' ')
        .trim()

      return [{
        dp: 'accommodation',
        checkin: item.checkIn,
        checkout: item.checkOut,
        accommodationType: 'HOTEL',
        name: offer?.name ?? '',
        address: {
          street,
          country: offer?.property?.address.countryCode ?? '',
          zip: offer?.property?.address.postalCode ?? '',
          city: offer?.property?.address.city ?? '',
        },
      }]
    })

    const flightQuoteItems = flightItems.flatMap((item) => {
      const flights = item.flights.flatMap((flight) => {
        if (!flight.journeyId || !item.searchId) return []

        const journeyKey = getJourneyV2IdKey(flight.journeyId, item.searchId, flight.fareFamily?.id)
        const journey = journeysById[journeyKey] as App.JourneyV2 | undefined
        if (!journey?.flightGroup) return []

        return journey.flightGroup.flights.map((segment: App.FlightV2) => ({
          arrivalDate: segment.arrivalDate,
          departureDate: segment.departingDate,
          flightId: segment.flightNumber,
          departureAirport: segment.departureAirport,
          arrivalTime: segment.arrivalTime,
          departureTime: segment.departingTime,
          arrivalAirport: segment.arrivalAirport,
        }))
      })
      if (!flights.length) return []
      return [{ dp: 'flight' as const, flights }]
    })

    const tourQuoteItems = tourV2Items.flatMap((item) => {
      if (!item.startDate || !item.endDate) return []

      const offer = offers[item.offerId]
      if (!isTourV2Offer(offer)) return []

      const variationId = item.purchasableOption.fkVariationId
      const variation = offer.variations[variationId]
      if (!variation) return []

      const departure = variation.departures.find(d => d.id === item.purchasableOption.fkDepartureId)
      if (!departure) return []

      const itineraryCountryCodes = variation.itinerary
        ? getTourV2CountryCodesVisited(variation.itinerary)
        : []
      const uniqueDestinations = [...new Set([
        variation.startLocationCountryCode,
        variation.endLocationCountryCode,
        ...itineraryCountryCodes,
      ].filter((c): c is string => !!c))]
        .map(code => findByTwoLetterCode(code))
        .filter((name): name is string => !!name)

      const departureTime = departure.startTimeLocal
      const arrivalTime = departure.endTimeLocal

      return [{
        dp: 'tour' as const,
        name: offer.name,
        departureCountry: findByTwoLetterCode(variation.startLocationCountryCode ?? '') ?? '',
        arrivalCountry: findByTwoLetterCode(variation.endLocationCountryCode ?? '') ?? '',
        departureDate: item.startDate,
        departureTime,
        arrivalDate: item.endDate,
        arrivalTime,
        destinations: uniqueDestinations,
      }]
    })

    const cruiseQuoteItems = cruiseItems.flatMap((item) => {
      if (!item.startDate || !item.endDate) return []

      const offer = offers[item.offerId]
      if (!isCruiseOffer(offer)) return []

      const departure = cruiseDepartureMap[item.departureId]

      const itinerary = offer.itinerary ?? []
      const portsOfCall = itinerary.filter(stop => stop.port != null)
      const firstPortStop = portsOfCall[0]
      const lastPortStop = portsOfCall[portsOfCall.length - 1]

      const countryCodes = getCruiseCountriesByItinerary(itinerary)
      const destinations = countryCodes
        .map(code => findByTwoLetterCode(code))
        .filter((name): name is string => !!name)

      const departureTime = sanitizeCruiseTime(firstPortStop?.endTime)
      const arrivalTime = sanitizeCruiseTime(lastPortStop?.startTime)

      return [{
        dp: 'cruise' as const,
        name: offer.name,
        departureCountry: findByTwoLetterCode(firstPortStop?.port?.countryCode ?? '') ?? '',
        arrivalCountry: findByTwoLetterCode(lastPortStop?.port?.countryCode ?? '') ?? '',
        departureDate: departure?.departureDate ?? item.startDate,
        departureTime,
        arrivalDate: departure?.returnDate ?? item.endDate,
        arrivalTime,
        destinations,
      }]
    })

    return [...leAccommodationQuoteItems, ...bedbankQuoteItems, ...flightQuoteItems, ...tourQuoteItems, ...cruiseQuoteItems]
  },
)

export const hasPassportCardEligibleItems = createSelector(getPassportCardQuoteItems, items => items.length > 0)

export default getPassportCardQuoteItems
